import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { z } from "zod";

import { m } from "@recobra/i18n/messages";
import type { BrazilAddress } from "@recobra/schemas/client";
import { Field, FieldDescription, FieldError, FieldLabel } from "@recobra/ui/field";
import { Input } from "@recobra/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";

import { getErrorMessage, useFieldContext } from "./context";
import { OptionalTextField } from "./OptionalTextField";

const cepAddressSchema = z.object({
  logradouro: z.string().optional(),
  bairro: z.string().optional(),
  localidade: z.string().optional(),
  uf: z.string().optional(),
});

type CepAddress = z.infer<typeof cepAddressSchema>;

const brasilApiCepSchema = z.object({
  street: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

const lookupViaCep = async (digits: string): Promise<CepAddress | null> => {
  const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
  if (!res.ok) return null;

  const data: unknown = await res.json();
  if (data && typeof data === "object" && "erro" in data) return null;

  const result = cepAddressSchema.safeParse(data);
  return result.success ? result.data : null;
};

const lookupBrasilApiCep = async (digits: string): Promise<CepAddress | null> => {
  const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${digits}`);
  if (!res.ok) return null;

  const data: unknown = await res.json();
  const result = brasilApiCepSchema.safeParse(data);
  if (!result.success) return null;

  return {
    logradouro: result.data.street,
    bairro: result.data.neighborhood,
    localidade: result.data.city,
    uf: result.data.state,
  };
};

const formatCep = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);

  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const CepInput: FC<{
  id: string;
  loading: boolean;
  notFound: boolean;
  onLookup: (cep: string) => void | Promise<void>;
  onChangeCep: () => void;
}> = ({ id, loading, notFound, onLookup, onChangeCep }) => {
  const field = useFieldContext<string | undefined>();
  const errorMessage = getErrorMessage(field.state.meta.errors[0]);

  return (
    <Field data-invalid={errorMessage ? "true" : undefined} className="min-w-0">
      <FieldLabel htmlFor={id}>{m.clients_field_address_cep()}</FieldLabel>
      <div className="relative">
        <Input
          id={id}
          value={field.state.value ?? ""}
          onChange={(e) => {
            const formatted = formatCep(e.target.value);
            field.handleChange(formatted || undefined);
            onChangeCep();
          }}
          onBlur={(e) => {
            field.handleBlur();
            void onLookup(e.target.value);
          }}
          placeholder={m.clients_field_address_cep_placeholder()}
          inputMode="numeric"
          maxLength={9}
          aria-invalid={Boolean(errorMessage)}
          autoComplete="postal-code"
        />
        {loading && (
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
      {!errorMessage && notFound ? (
        <FieldDescription>{m.clients_field_address_cep_not_found()}</FieldDescription>
      ) : null}
    </Field>
  );
};

const BRAZIL_STATES = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraima" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" },
] as const;

const StateSelectField: FC = () => {
  const field = useFieldContext<string | undefined>();
  const errorMessage = getErrorMessage(field.state.meta.errors[0]);

  return (
    <Field data-invalid={errorMessage ? "true" : undefined} className="min-w-0">
      <FieldLabel>{m.clients_field_address_state()}</FieldLabel>
      <Select
        value={field.state.value ?? ""}
        onValueChange={(v) => field.handleChange(v || undefined)}
      >
        <SelectTrigger className="w-full" aria-invalid={Boolean(errorMessage)}>
          <SelectValue placeholder={m.clients_select_placeholder()} />
        </SelectTrigger>
        <SelectContent>
          {BRAZIL_STATES.map(({ value, label }) => (
            <SelectItem key={value} value={value}>
              {label} ({value})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </Field>
  );
};

interface AddressGroupApi {
  AppField: FC<PropsWithChildren<{ name: keyof BrazilAddress }>>;
  setFieldValue: (field: keyof BrazilAddress, value: string | undefined) => void;
}

const AddressGroupBody: FC<{ group: AddressGroupApi; idPrefix: string }> = ({
  group,
  idPrefix,
}) => {
  const mutation = useMutation({
    mutationFn: async (digits: string) => {
      return (await lookupViaCep(digits)) ?? (await lookupBrasilApiCep(digits));
    },
    onSuccess: (data) => {
      if (!data) return;

      group.setFieldValue("street", data.logradouro || undefined);
      group.setFieldValue("district", data.bairro || undefined);
      group.setFieldValue("city", data.localidade || undefined);
      group.setFieldValue("state", data.uf || undefined);
    },
    meta: {
      suppressErrorToast: true,
    },
  });

  const lookupCep = useCallback(
    (rawCep: string) => {
      const digits = rawCep.replace(/\D/g, "");
      if (digits.length !== 8) return;
      mutation.mutate(digits);
    },
    [mutation],
  );

  const { AppField } = group;

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <AppField name="cep">
          <CepInput
            id={`${idPrefix}-cep`}
            loading={mutation.isPending}
            notFound={mutation.isSuccess && mutation.data === null}
            onLookup={lookupCep}
            onChangeCep={mutation.reset}
          />
        </AppField>
        <div className="sm:col-span-2">
          <AppField name="street">
            <OptionalTextField
              label={m.clients_field_address_street()}
              id={`${idPrefix}-street`}
              maxLength={200}
            />
          </AppField>
        </div>
        <AppField name="number">
          <OptionalTextField
            label={m.clients_field_address_number()}
            id={`${idPrefix}-number`}
            maxLength={20}
          />
        </AppField>
      </div>

      <AppField name="complement">
        <OptionalTextField
          label={m.clients_field_address_complement()}
          id={`${idPrefix}-complement`}
          maxLength={100}
        />
      </AppField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <AppField name="district">
          <OptionalTextField
            label={m.clients_field_address_district()}
            id={`${idPrefix}-district`}
            maxLength={100}
          />
        </AppField>
        <AppField name="city">
          <OptionalTextField
            label={m.clients_field_address_city()}
            id={`${idPrefix}-city`}
            maxLength={100}
          />
        </AppField>
        <AppField name="state">
          <StateSelectField />
        </AppField>
      </div>
    </div>
  );
};

export const addressFieldGroupDefaults: BrazilAddress = {
  cep: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
};

export { type AddressGroupApi, AddressGroupBody };
